import { BadgeDelta, Text } from '@tremor/react';
import type { DeltaType } from '@tremor/react';
import { formatMxn } from '../lib/format';

interface Props {
  currentLabel: string;
  priorLabel: string;
  /** Totals for the current period (typically already to-date because the
   *  daemon walks tickets where idate <= today). */
  currentMxn: number;
  /** Totals for the *prior* period, sliced to the same to-date position so
   *  the comparison is apples-to-apples. Callers do the slicing — the
   *  bucket-index → "now" mapping varies per period. */
  priorMxn: number;
  /** When true, render a tiny "a la fecha" qualifier so the operator knows
   *  the prior side was sliced (not the full historical period). Suppress
   *  for closed periods where current == prior shape (rare on our dashboard
   *  but kept for symmetry). */
  partial?: boolean;
}

/**
 * One-line stat strip rendered above the bar chart. Reads:
 *   Junio 2026: $1,234,500    Junio 2025 (a la fecha): $1,050,200   [↑ +$184,300 (+17.5%)]
 *
 * Delta is current - prior; sign + percentage drive the BadgeDelta color
 * (Tremor handles green/red mapping). When prior is zero the percentage
 * is dropped so we don't render "+Infinity%".
 */
export default function SalesDeltaSummary({
  currentLabel, priorLabel, currentMxn, priorMxn, partial,
}: Props) {
  const delta = currentMxn - priorMxn;
  const pct = priorMxn !== 0 ? (delta / priorMxn) * 100 : null;
  const dt: DeltaType =
    delta > priorMxn * 0.02 ? 'moderateIncrease'
    : delta < -priorMxn * 0.02 ? 'moderateDecrease'
    : 'unchanged';

  const deltaText = pct === null
    ? formatMxn(delta)
    : `${delta >= 0 ? '+' : ''}${formatMxn(delta)} (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)`;

  return (
    <div className="mt-2 flex items-baseline gap-4 flex-wrap">
      <div>
        <Text className="text-tremor-content text-xs">{currentLabel}</Text>
        <div className="tabular-nums font-semibold">{formatMxn(currentMxn)}</div>
      </div>
      <div>
        <Text className="text-tremor-content text-xs">
          {priorLabel}{partial ? ' (a la fecha)' : ''}
        </Text>
        <div className="tabular-nums text-tremor-content">{formatMxn(priorMxn)}</div>
      </div>
      <BadgeDelta deltaType={dt}>{deltaText}</BadgeDelta>
    </div>
  );
}
